import React, { useEffect, useRef, useState } from "react";
import { Badge } from "@amzn/awsui-components-react-v3";
import Tooltip from "@amzn/awsui-components-console/tooltip";
import { formatRelativeTime } from "../utils/time";
import { UserStatus } from "../services/idp/idp.types";

interface LastAuthenticationProps {
  time?: string;
  userStatus: string;
}

/**
 * Returns the badge color based on the last authentication time and user status
 * @param time - The time the user last authenticated
 * @param userStatus - The status of the user
 * @returns A valid badge color option for the Cloudscape Badge component
 */
const getBadgeColor = (
  time: string | undefined,
  userStatus: string,
):
  | "grey"
  | "green"
  | "severity-low"
  | "severity-medium"
  | "severity-high"
  | "severity-critical" => {
  if (userStatus === UserStatus.Disabled || !time) return "grey";

  const diffInDays = Math.abs(
    (new Date().getTime() - new Date(time).getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays < 1) return "green";
  if (diffInDays < 7) return "severity-low";
  if (diffInDays < 30) return "severity-medium";
  if (diffInDays < 90) return "severity-high";
  return "severity-critical";
};

export const LastAuthenticationBadge = ({
  time,
  userStatus,
}: LastAuthenticationProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [, setTick] = useState(0);

  // if the last auth time is within the past hour, automatically refresh the relative time on a minute interval
  useEffect(() => {
    if (!time) return;
    const diffMs = Math.abs(new Date().getTime() - new Date(time).getTime());
    // after an hour, clear the interval
    if (diffMs >= 3600000) return;
    const remainingMs = 3600000 - diffMs;
    const intervalId = setInterval(() => setTick((t) => t + 1), 60000);
    const timeoutId = setTimeout(() => clearInterval(intervalId), remainingMs);
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [time]);
  const tooltipContent = time
    ? new Date(time).toLocaleString(undefined, { timeZoneName: "short" })
    : "User has not yet authenticated with Frontier";

  return (
    <span
      ref={ref}
      tabIndex={0} // for keyboard navigation users
      onPointerEnter={() => setTooltipVisible(true)}
      onPointerLeave={() => setTooltipVisible(false)}
      onFocus={() => setTooltipVisible(true)}
      onBlur={() => setTooltipVisible(false)}
    >
      <Badge color={getBadgeColor(time, userStatus)}>
        {formatRelativeTime(time)}
      </Badge>
      {tooltipVisible && (
        <span data-testid="last-auth-tooltip">
          {/* hidden span is recommended by Cloudscape for accessibility with screen readers; also helps with testing*/}
          <span hidden>{tooltipContent}</span>
          <Tooltip
            content={tooltipContent}
            getTrack={() => ref.current}
            onEscape={() => setTooltipVisible(false)}
            position="right"
          />
        </span>
      )}
    </span>
  );
};
